import { AfterViewInit, Component, inject, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { NavigationStart, Router } from '@angular/router';
import { CommentsService } from '@k12/comments';
import { Comment } from '@k12/models';
import { CommentComponent } from '@k12/ui-primitives';
import { filter, Subject, takeUntil } from 'rxjs';
import { SideDrawerService } from './side-drawer.service';

export interface SideDrawerConfig<TData = unknown> {
  component: Type<unknown>;
  componentData?: TData;
  componentName?: string;
  disablebodyScroll?: boolean;
  disableClose?: boolean;
  drawerMode?: 'over' | 'side';
  drawerPosition?: 'start' | 'end';
  isBackdrop?: boolean;
  showCloseButton?: boolean;
  title: string;
}

enum ComponentName {
  Comments = 'Comments'
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-side-drawer',
  imports: [CommentComponent, MatButtonModule, MatIconModule, MatSidenavModule],
  templateUrl: './side-drawer.component.html',
  styleUrls: ['./side-drawer.component.scss']
})
export class SideDrawerComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;
  @ViewChild('drawer') drawer!: MatSidenav;

  componentData: unknown;
  comments: Comment[] = [];
  componentName: string = 'Default';
  ComponentName = ComponentName;
  disableClose: boolean = true;
  drawerMode: 'over' | 'side' = 'over';
  drawerPosition: 'start' | 'end' = 'end';
  isBackdrop: boolean = false;
  showCloseButton: boolean = true;
  title: string = 'Drawer Title';

  private readonly commentsService = inject(CommentsService);
  private readonly destroy$ = new Subject<void>();
  private readonly sideDrawerService = inject(SideDrawerService);
  private readonly router = inject(Router);

  ngOnInit(): void {
    // Internal Subject; cannot error by construction, so no error handler is needed.
    this.commentsService.refreshComments$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: { action: string; comment?: Comment }) => {
        if (payload.action === 'delete' && payload.comment) {
          this.onDeleteComment(payload.comment);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Internal BehaviorSubject; cannot error by construction, so no error handler is needed.
    this.sideDrawerService.config$.subscribe((config: SideDrawerConfig | null) => {
      if (!config) {
        this.drawer.close();
        this.container.clear();
        this.comments = [];
        return;
      }
      this.title = config.title ?? 'Drawer Title';
      this.drawerMode = config.drawerMode ?? 'over';
      this.drawerPosition = config.drawerPosition ?? 'end';
      this.showCloseButton = config.showCloseButton ?? true;
      this.isBackdrop = config.isBackdrop ?? false;
      this.disableClose = config.disableClose ?? true;
      this.container.clear();
      this.container.createComponent(config.component);
      this.componentName = config.componentName ?? 'Default';
      this.componentData = config.componentData ?? undefined;
      this.comments = Array.isArray(config.componentData) ? (config.componentData as Comment[]) : [];
      this.drawer.open();
      if (config.disablebodyScroll) {
        document.body.style.overflow = 'hidden';
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.onDrawerClosed();
    });
  }

  onDrawerClosed(): void {
    document.body.style.overflow = 'auto';
    this.sideDrawerService.closeDrawer();
  }

  onDeleteComment(comment: Comment): void {
    this.comments = this.comments.filter((c) => c.id !== comment.id);
  }
}
